// FILE: PlaneSection.tsx
// Purpose: Settings → Plane. The installation's Plane connection, and which board
//          each Taskin project tracks its work on.
// Layer: Web settings presentation
// Exports: PlaneSection
//
// Two blocks, in the order they have to be configured: the connection first —
// nothing else can be done without a token — then one row per project.
//
// Tokens are write-only in both directions, exactly as in `ProjectRepoSection`:
// the password fields start empty even when a token is stored, because there is
// nothing to rehydrate. What the screen shows instead is the verdict
// (`Sin probar` / `Conectado` / `Error`) and, when a call failed, what Plane said.
//
// There is no per-project credential any more, and no field for one: an agent
// acts with the Plane account of the human who owns the thread, configured by
// that person in Settings → Profile. What a project chooses here is *where* its
// board lives — workspace and board — while *who* reaches it is a property of
// each user. The last block of the screen shows who has configured their account,
// because an admin who links a board still needs to know whom to chase.

import { useCallback, useEffect, useState } from "react";

import type {
  AdminUserPlaneCredentialRow,
  PlaneConnectionView,
  PlaneCredentialStatus,
  PlaneProjectSummary,
  ProjectId,
} from "@taskin-code/contracts";

import { useAuthStore } from "../../auth/authStore";
import { PlaneClient } from "../../lib/planeClient";
import { refreshShellSnapshot } from "../../lib/shellSnapshot";
import { useStore } from "../../store";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";

const STATUS_LABELS: Record<PlaneCredentialStatus, string> = {
  none: "Sin probar",
  valid: "Conectado",
  invalid: "Error",
};

const STATUS_BADGE: Record<PlaneCredentialStatus, string> = {
  none: "bg-muted/50 text-muted-foreground",
  valid: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300",
  invalid: "bg-red-500/15 text-red-600 dark:text-red-300",
};

const STATUS_DOT: Record<PlaneCredentialStatus, string> = {
  none: "bg-muted-foreground/40",
  valid: "bg-emerald-500",
  invalid: "bg-red-500",
};

/** Sentinel for the picker's "no board" option — an empty string cannot be a Select value. */
const UNLINKED = "__unlinked__";

function StatusBadge({ status }: { readonly status: PlaneCredentialStatus }) {
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-medium ${STATUS_BADGE[status]}`}
    >
      <span className={`size-1.5 rounded-full ${STATUS_DOT[status]}`} aria-hidden="true" />
      {STATUS_LABELS[status]}
    </span>
  );
}

/**
 * Which board one project follows.
 *
 * Split out from `PlaneSection` because only this half is about a project. The
 * connection and the per-user credentials are installation-wide: repointing the
 * Plane instance changes every project at once, so it stays behind the global
 * grant and out of a per-project tab. The server draws the same line — see
 * `planeAdminRoutes`.
 */
export function PlaneProjectSection(props: { readonly projectId: ProjectId }) {
  return <PlaneProjectLinksPanel projectId={props.projectId} />;
}

export function PlaneSection() {
  const canConfigure = useAuthStore((state) =>
    state.globalPermissions.has("p.project.set_plane_credentials"),
  );

  if (!canConfigure) {
    return (
      <div className="rounded-md border border-border/60 bg-card/40 p-6 text-sm text-muted-foreground">
        Necesitas el permiso <code>p.project.set_plane_credentials</code> para configurar Plane.
      </div>
    );
  }

  // The per-project links are deliberately absent: they live in
  // Settings → Proyectos → Plane, next to everything else about a project.
  return (
    <div className="flex flex-col gap-8">
      <PlaneConnectionPanel />
      <PlaneUserCredentialsPanel />
    </div>
  );
}

function PlaneConnectionPanel() {
  const [connection, setConnection] = useState<PlaneConnectionView | null>(null);
  const [baseUrl, setBaseUrl] = useState("");
  const [workspaceSlug, setWorkspaceSlug] = useState("");
  const [token, setToken] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const apply = useCallback((value: PlaneConnectionView) => {
    setConnection(value);
    setBaseUrl(value.baseUrl);
    setWorkspaceSlug(value.workspaceSlug);
    // Never rehydrated: the server does not return it, and an empty field is the
    // honest representation of "we hold one but you cannot see it".
    setToken("");
  }, []);

  useEffect(() => {
    let cancelled = false;
    void PlaneClient.readConnection()
      .then((value) => {
        if (!cancelled) apply(value);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setError(cause instanceof Error ? cause.message : "No se pudo leer la conexión de Plane.");
      });
    return () => {
      cancelled = true;
    };
  }, [apply]);

  const save = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSaving) return;
    if (baseUrl.trim() === "" || workspaceSlug.trim() === "") {
      setError("La URL y el workspace son obligatorios.");
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      const stored = await PlaneClient.writeConnection({
        baseUrl: baseUrl.trim(),
        workspaceSlug: workspaceSlug.trim(),
        // Omitted rather than sent empty: an empty string means "disconnect",
        // and leaving the field alone must not do that.
        ...(token.trim() === "" ? {} : { token: token.trim() }),
      });
      apply(stored);
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : "No se pudo guardar la conexión.");
    } finally {
      setIsSaving(false);
    }
  };

  const disconnect = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const stored = await PlaneClient.writeConnection({
        baseUrl: baseUrl.trim(),
        workspaceSlug: workspaceSlug.trim(),
        token: "",
      });
      apply(stored);
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : "No se pudo borrar el token.");
    } finally {
      setIsSaving(false);
    }
  };

  if (connection === null) {
    return (
      <p className="rounded-xl border border-border/70 px-4 py-3 text-xs text-muted-foreground">
        {error ?? "Cargando la conexión de Plane…"}
      </p>
    );
  }

  return (
    <section className="flex flex-col gap-4 rounded-xl border border-border/60 bg-card/40 p-5">
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold">Conexión con Plane</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            La instancia de Plane y el workspace por defecto. El token es solo de administración:
            sirve para poder elegir tablero aquí abajo cuando quien lo hace todavía no ha
            configurado su cuenta de Plane. Nada de lo que hacen los agentes pasa por él — cada
            comentario, estado y página sale con la credencial de la persona dueña del hilo.
          </p>
        </div>
        <StatusBadge status={connection.tokenStatus} />
      </header>

      {connection.tokenStatus === "invalid" && connection.tokenLastError !== null ? (
        <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-600 dark:text-red-300">
          <span className="font-medium">No conecta:</span> {connection.tokenLastError}
        </div>
      ) : null}

      <form onSubmit={save} className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="plane-base-url">URL de la instancia</Label>
          <Input
            id="plane-base-url"
            type="text"
            placeholder="https://pm.midasred.com"
            value={baseUrl}
            disabled={isSaving}
            onChange={(e) => setBaseUrl(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="plane-workspace">Workspace</Label>
          <Input
            id="plane-workspace"
            type="text"
            placeholder="departamento-ingenieria"
            value={workspaceSlug}
            disabled={isSaving}
            onChange={(e) => setWorkspaceSlug(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
          <p className="text-[11px] text-muted-foreground">
            El slug tal como aparece en la URL de Plane.
          </p>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="plane-token">Token de API</Label>
          <Input
            id="plane-token"
            type="password"
            placeholder={connection.tokenStatus === "none" ? "plane_api_..." : "•••••••• guardado"}
            value={token}
            disabled={isSaving}
            onChange={(e) => setToken(e.target.value)}
            autoComplete="new-password"
            spellCheck={false}
          />
          <p className="text-[11px] text-muted-foreground">
            Opcional. Genéralo en Plane → avatar → Settings → Personal Access Tokens y pégalo aquí;
            déjalo vacío para conservar el actual. Solo se usa para listar tableros, y solo si quien
            está vinculando no tiene su propia cuenta de Plane configurada en Ajustes → Perfil.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button type="submit" size="sm" disabled={isSaving}>
            {isSaving ? "Guardando…" : "Guardar"}
          </Button>
          {connection.tokenStatus !== "none" ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={isSaving}
              onClick={() => void disconnect()}
            >
              Borrar token
            </Button>
          ) : null}
        </div>
      </form>

      {error !== null ? <p className="text-xs text-red-400">{error}</p> : null}
    </section>
  );
}

function PlaneProjectLinksPanel(props: { readonly projectId?: ProjectId }) {
  const allProjects = useStore((state) => state.projects);
  const projects =
    props.projectId === undefined
      ? allProjects
      : allProjects.filter((project) => project.id === props.projectId);
  const [boards, setBoards] = useState<ReadonlyArray<PlaneProjectSummary> | null>(null);
  const [defaultWorkspace, setDefaultWorkspace] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void PlaneClient.readConnection()
      .then((connection) => {
        if (!cancelled) setDefaultWorkspace(connection.workspaceSlug);
      })
      .catch(() => {
        /* The connection panel above already reports this. */
      });
    void PlaneClient.listProjects()
      .then((rows) => {
        if (!cancelled) setBoards(rows);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        // The usual cause is no admin token yet, which the connection panel above
        // is already asking for — so this reads as a prerequisite, not a failure.
        setError(
          cause instanceof Error ? cause.message : "No se pudieron listar los proyectos de Plane.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (projects.length === 0) {
    return (
      <div className="rounded-md border border-border/60 bg-card/40 p-6 text-sm text-muted-foreground">
        No hay proyectos todavía. Crea uno desde el sidebar y vuelve aquí para vincularlo con su
        tablero de Plane.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="min-w-0">
        {props.projectId === undefined && (
          <h3 className="text-base font-semibold">Tablero por proyecto</h3>
        )}
        <p className="mt-0.5 text-xs text-muted-foreground">
          Los agentes de un hilo solo pueden leer y escribir en el tablero de su propio proyecto. Un
          proyecto sin vincular no recibe las herramientas de Plane en absoluto. Si el tablero vive
          en otro workspace de la misma instancia, indícalo en la fila.
        </p>
      </div>

      {error !== null ? (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
          {error}
        </div>
      ) : null}

      <div className="flex flex-col gap-4">
        {projects.map((project) => (
          <PlaneProjectLinkRow
            key={project.id}
            projectId={project.id}
            projectName={project.name}
            defaultBoards={boards}
            defaultWorkspaceSlug={defaultWorkspace}
            initialPlaneProjectId={project.planeProjectId ?? null}
            initialWorkspaceSlug={project.planeWorkspaceSlug ?? null}
          />
        ))}
      </div>
    </div>
  );
}

interface PlaneProjectLinkRowProps {
  readonly projectId: string;
  readonly projectName: string;
  /** Boards of the installation's own workspace, loaded once by the panel. */
  readonly defaultBoards: ReadonlyArray<PlaneProjectSummary> | null;
  readonly defaultWorkspaceSlug: string;
  readonly initialPlaneProjectId: string | null;
  readonly initialWorkspaceSlug: string | null;
}

function PlaneProjectLinkRow({
  projectId,
  projectName,
  defaultBoards,
  defaultWorkspaceSlug,
  initialPlaneProjectId,
  initialWorkspaceSlug,
}: PlaneProjectLinkRowProps) {
  // `null` means "follow the projection". Anything else is an unsaved local edit.
  const [draftBoardId, setDraftBoardId] = useState<string | null>(null);
  const [workspaceDraft, setWorkspaceDraft] = useState(initialWorkspaceSlug ?? "");
  const [ownBoards, setOwnBoards] = useState<ReadonlyArray<PlaneProjectSummary> | null>(null);
  const [boardsError, setBoardsError] = useState<string | null>(null);
  const [isLoadingBoards, setIsLoadingBoards] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const usesOwnWorkspace = workspaceDraft.trim().length > 0;
  const boards = usesOwnWorkspace ? ownBoards : defaultBoards;
  const selectedBoardId = draftBoardId ?? initialPlaneProjectId ?? UNLINKED;
  const selectedBoard = boards?.find((board) => board.id === selectedBoardId) ?? null;

  const loadBoards = useCallback(async (slug: string) => {
    setIsLoadingBoards(true);
    setBoardsError(null);
    try {
      setOwnBoards(await PlaneClient.listProjects(slug));
    } catch (cause: unknown) {
      setOwnBoards([]);
      setBoardsError(
        cause instanceof Error
          ? cause.message
          : `No se pudieron listar los tableros del workspace ${slug}.`,
      );
    } finally {
      setIsLoadingBoards(false);
    }
  }, []);

  // A project already linked to another workspace needs its boards on first
  // paint, or the select would render its saved board as "Sin vincular".
  useEffect(() => {
    const slug = initialWorkspaceSlug?.trim() ?? "";
    if (slug.length > 0) void loadBoards(slug);
  }, [initialWorkspaceSlug, loadBoards]);

  const save = async () => {
    if (isSaving) return;
    setIsSaving(true);
    setMessage(null);
    try {
      const linked = selectedBoardId !== UNLINKED;
      const result = await PlaneClient.writeProjectLink(projectId, {
        planeProjectId: linked ? selectedBoardId : null,
        planeProjectIdentifier: linked ? (selectedBoard?.identifier ?? null) : null,
        workspaceSlug: linked && usesOwnWorkspace ? workspaceDraft.trim() : null,
      });
      setDraftBoardId(result.planeProjectId ?? UNLINKED);
      setWorkspaceDraft(result.planeWorkspaceSlug ?? "");
      setMessage(linked ? "Vínculo guardado." : "Proyecto desvinculado.");
      // The link lives outside the event log, so nothing else would tell the
      // sidebar that this project can now open threads from an issue.
      refreshShellSnapshot();
    } catch (cause: unknown) {
      setMessage(cause instanceof Error ? cause.message : "No se pudo guardar el vínculo.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section
      className={`flex flex-col gap-3 rounded-xl border border-l-[3px] bg-card/40 p-4 transition-colors ${
        selectedBoardId === UNLINKED
          ? "border-border/60 border-l-muted-foreground/30"
          : "border-border/60 border-l-brand/70"
      }`}
    >
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="truncate text-[15px] font-semibold tracking-tight text-brand-foreground">
            {projectName}
          </h4>
          <p className="mt-0.5 truncate font-mono text-[11px] text-muted-foreground/70">
            {selectedBoard
              ? `${selectedBoard.identifier} · ${selectedBoard.name} · ${
                  usesOwnWorkspace ? workspaceDraft.trim() : defaultWorkspaceSlug
                }`
              : "Sin tablero vinculado"}
          </p>
        </div>
      </header>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex min-w-0 flex-col gap-1.5">
          <Label htmlFor={`plane-workspace-${projectId}`}>Workspace</Label>
          <div className="flex items-center gap-2">
            <Input
              id={`plane-workspace-${projectId}`}
              className="w-full sm:w-56"
              placeholder={defaultWorkspaceSlug || "workspace por defecto"}
              value={workspaceDraft}
              disabled={isSaving}
              spellCheck={false}
              onChange={(event) => {
                setWorkspaceDraft(event.target.value);
              }}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={isSaving || isLoadingBoards || !usesOwnWorkspace}
              onClick={() => void loadBoards(workspaceDraft.trim())}
            >
              {isLoadingBoards ? "Cargando…" : "Cargar tableros"}
            </Button>
          </div>
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <Label htmlFor={`plane-board-${projectId}`}>Tablero de Plane</Label>
          <Select
            value={selectedBoardId}
            onValueChange={(value) => {
              if (!value) return;
              setDraftBoardId(value);
            }}
          >
            <SelectTrigger
              id={`plane-board-${projectId}`}
              className="w-full sm:w-80"
              disabled={isSaving || boards === null}
              aria-label={`Tablero de Plane para ${projectName}`}
            >
              <SelectValue>
                {selectedBoard
                  ? `${selectedBoard.identifier} — ${selectedBoard.name}`
                  : boards === null
                    ? "Cargando tableros…"
                    : "Sin vincular"}
              </SelectValue>
            </SelectTrigger>
            <SelectPopup align="start" alignItemWithTrigger={false}>
              <SelectItem hideIndicator value={UNLINKED}>
                Sin vincular
              </SelectItem>
              {(boards ?? []).map((board) => (
                <SelectItem hideIndicator key={board.id} value={board.id}>
                  {board.identifier} — {board.name}
                </SelectItem>
              ))}
            </SelectPopup>
          </Select>
        </div>
      </div>

      <p className="text-[11px] text-muted-foreground">
        Deja el workspace vacío para usar el de la instalación
        {defaultWorkspaceSlug ? ` (${defaultWorkspaceSlug})` : ""}. Escribe otro y pulsa «Cargar
        tableros» para vincular un tablero que viva fuera de él.
      </p>

      {boardsError !== null ? (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
          {boardsError}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2 pt-0.5">
        <Button type="button" size="sm" disabled={isSaving} onClick={() => void save()}>
          {isSaving ? "Guardando…" : "Guardar"}
        </Button>
      </div>

      {message !== null ? <p className="text-xs text-muted-foreground">{message}</p> : null}
    </section>
  );
}

/**
 * Who has configured their Plane account.
 *
 * Statuses only, and read-only by design: an admin needs to know whom to chase
 * when an agent reports it cannot touch the board, but nobody may type someone
 * else's password — a credential an admin could set would make "posted by X"
 * mean nothing. Somebody missing from this list simply has not configured Plane;
 * their agents will say so when they try.
 */
function PlaneUserCredentialsPanel() {
  const [rows, setRows] = useState<ReadonlyArray<AdminUserPlaneCredentialRow> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void PlaneClient.listUserCredentials()
      .then((value) => {
        if (!cancelled) setRows(value);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setError(
          cause instanceof Error ? cause.message : "No se pudieron leer las credenciales de Plane.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="flex flex-col gap-4">
      <div className="min-w-0">
        <h3 className="text-base font-semibold">Cuentas de Plane del equipo</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Cada persona configura la suya en Ajustes → Perfil, y es la cuenta con la que sus agentes
          comentan y escriben. Aquí solo se ve el estado: nadie, ni tú, puede escribir la credencial
          de otra persona.
        </p>
      </div>

      {error !== null ? (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
          {error}
        </div>
      ) : null}

      {rows === null ? (
        <p className="text-xs text-muted-foreground">Cargando…</p>
      ) : rows.length === 0 ? (
        <div className="rounded-md border border-border/60 bg-card/40 p-6 text-sm text-muted-foreground">
          Nadie ha configurado su cuenta de Plane todavía. Hasta que lo hagan, sus agentes no pueden
          leer ni escribir en los tableros.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[32rem] text-left text-xs">
            <thead className="text-muted-foreground">
              <tr>
                <th className="py-1.5 pr-4 font-medium">Cuenta de Plane</th>
                <th className="py-1.5 pr-4 font-medium">Estado</th>
                <th className="py-1.5 pr-4 font-medium">Token emitido</th>
                <th className="py-1.5 font-medium">Último error</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.userId} className="border-t border-border/50">
                  <td className="py-1.5 pr-4 font-mono">{row.planeEmail ?? "—"}</td>
                  <td className="py-1.5 pr-4">
                    <StatusBadge status={row.status} />
                  </td>
                  <td className="py-1.5 pr-4">{row.hasApiToken ? "Sí" : "No"}</td>
                  <td className="py-1.5 text-muted-foreground">{row.lastError ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
